import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, Save, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminEmailConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_use_tls: true,
    bcc_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/email/smtp-config`,
        getAuthHeaders()
      );
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      alert('Erro ao carregar configurações SMTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/email/smtp-config`,
        config,
        getAuthHeaders()
      );
      alert('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/email/test-smtp`,
        {},
        getAuthHeaders()
      );
      setTestResult({
        success: response.data.success,
        message: response.data.message
      });
    } catch (error) {
      console.error('Erro ao testar:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Erro ao enviar email de teste'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#00A86B]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              Configurações do SMTP
            </h1>
          </div>
        </div>

        <Card className="p-6">
          <div className="space-y-6">
            {/* SMTP Host */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Servidor SMTP
              </label>
              <Input
                type="text"
                value={config.smtp_host}
                onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                placeholder="smtp.exemplo.com"
                className="w-full"
              />
            </div>

            {/* SMTP Port */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Porta
              </label>
              <Input
                type="number"
                value={config.smtp_port}
                onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) })}
                placeholder="587"
                className="w-full"
              />
            </div>

            {/* SMTP User */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usuário SMTP
              </label>
              <Input
                type="text"
                value={config.smtp_user}
                onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                placeholder="usuario@exemplo.com"
                className="w-full"
              />
            </div>

            {/* SMTP Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senha SMTP
              </label>
              <Input
                type="password"
                value={config.smtp_password}
                onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                placeholder="••••••••"
                className="w-full"
              />
            </div>

            {/* Use TLS */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="use_tls"
                checked={config.smtp_use_tls}
                onChange={(e) => setConfig({ ...config, smtp_use_tls: e.target.checked })}
                className="w-4 h-4 text-[#00A86B] rounded focus:ring-[#00A86B]"
              />
              <label htmlFor="use_tls" className="text-sm font-medium text-gray-700">
                Usar TLS/SSL
              </label>
            </div>

            {/* BCC Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email para Cópia Oculta (BCC)
              </label>
              <Input
                type="email"
                value={config.bcc_email}
                onChange={(e) => setConfig({ ...config, bcc_email: e.target.value })}
                placeholder="copia@exemplo.com"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Todos os emails enviados aos usuários incluirão uma cópia oculta para este endereço
              </p>
            </div>

            {/* Test Result */}
            {testResult && (
              <div
                className={`p-4 rounded-lg ${
                  testResult.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <p
                  className={`text-sm ${
                    testResult.success ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {testResult.message}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleTest}
                disabled={testing}
                variant="outline"
                className="flex items-center gap-2"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {testing ? 'Enviando...' : 'Testar Configuração'}
              </Button>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-[#00A86B] hover:bg-[#00A86B]/90"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Informação:</strong> O email de teste será enviado para o endereço configurado no campo &quot;Cópia Oculta (BCC)&quot;.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminEmailConfig;
